import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IdObject } from 'src/app/core/id-object';
import { environment } from 'src/environments/environment';
import { Template, TemplateDraft, TemplateSearchData } from './template';
import {Deposit, DepositSearchData} from "../deposit/deposit";
import {PagedResponse, Paging, PagingHeader} from "../../core/paging/paging";

@Injectable({
  providedIn: 'root',
})
export class TemplateService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}template`;
  private static singleUrl = (apiUrl: string, id: string): string => `${apiUrl}template/${id}`;
  private static searchUrl = (apiUrl: string): string => `${apiUrl}template/search`;

  constructor(private httpClient: HttpClient, private logger: NGXLogger) {
  }

  public getList(): Observable<Template[]> {
    const {apiUrl} = environment;
    const url = TemplateService.listUrl(apiUrl);

    return this.httpClient
      .get<Template[]>(url)
      .pipe(catchError(this.handleError<Template[]>('TemplateService->getList', [])));
  }

  public getSingle(id: string): Observable<Template> {
    const {apiUrl} = environment;
    const url = TemplateService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<Template>(url)
      .pipe(catchError(this.handleError<Template>('TemplateService->getSingle')));
  }

  public create(templateDraft: TemplateDraft): Observable<string> {
    const {apiUrl} = environment;
    const url = TemplateService.listUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.post<IdObject>(url, templateDraft, {headers}).pipe(
      map((idObject: IdObject) => idObject.id),
      catchError(this.handleError<string>('TemplateService->create')),
    );
  }

  public patch(id: string, templateDraft: TemplateDraft): Observable<boolean> {
    const {apiUrl} = environment;
    const url = TemplateService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});

    return this.httpClient.patch<void>(url, templateDraft, {headers}).pipe(
      map(() => true),
      catchError(this.handleError<boolean>('TemplateService->patch', false)),
    );
  }

  public remove(id: string): Observable<boolean> {
    const {apiUrl} = environment;
    const url = TemplateService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('TemplateService->remove', false)),
    );
  }

  public search(
    templateSearchData: TemplateSearchData,
    paging?: Paging,
  ): Promise<PagedResponse<Template[]>> {
    const {apiUrl} = environment;
    const url = TemplateService.searchUrl(apiUrl);
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    const params = paging ? new HttpParams()
      .set('page', paging.currentPage)
      .set('sort', paging.sort)
      .set('sortDirection', paging.sortDirection)
      .set('perPage', paging.perPage) : undefined;

    return new Promise(async resolve => {
      this.httpClient
        .post<Template[]>(
          url,
          templateSearchData,
          {params, headers, observe: 'response'},
        )
        .pipe(catchError(this.handleError<HttpResponse<Template[]>>(
          'TemplateService->search',
          undefined,
        )))
        .subscribe((r) => {
          console.log('DONE - Response:', r.body);
          if (r.body) {
            const pagingHeader: PagingHeader = this.getPaging(r.headers);
            resolve({pagingHeader, items: r.body});
          }
        });
    });
  }

  getPaging(headers: HttpHeaders): PagingHeader {
    if (headers && headers.has('pagination-countavailable')) {
      const totalTemplates = headers.get('pagination-count');
      const perPage = headers.get('pagination-limit');
      const currentPage = headers.get('pagination-page');
      const hasNextPage = headers.get('pagination-nextpage');

      if (totalTemplates && perPage && currentPage && hasNextPage) {
        return {
          totalItems: parseInt(totalTemplates),
          perPage: parseInt(perPage),
          currentPage: parseInt(currentPage),
          hasNextPage: hasNextPage === 'true',
        };
      }
    }

    return {
      totalItems: 0, perPage: -1, currentPage: 0, hasNextPage: true,
    };
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      this.logger.error(`${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
